import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authClient } from "@/backend";
import { base44 } from "@/api/base44Client";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";

export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Completing email verification...");

  useEffect(() => {
    let active = true;

    const finish = async () => {
      if (!authClient.configured) {
        if (active) {
          setStatus("error");
          setMessage("Supabase authentication is not configured.");
        }
        return;
      }

      // Supabase handles the verification/session exchange when
      // detectSessionInUrl is enabled. Give it a moment to process the URL.
      const { data, error } = await authClient.getSession();
      if (!active) return;

      if (error) {
        setStatus("error");
        setMessage(error.message);
        return;
      }

      if (data.session) {
        let invited = false;
        try {
          const metadata = data.session.user?.user_metadata || {};
          invited = Boolean(metadata.dlp_local_user_id);
          if (invited) await base44.functions.invoke("acceptInvite", {});
        } catch (inviteError) {
          if (invited) {
            setStatus("error");
            setMessage(inviteError?.message || "We could not bind this email to the invited DrillLogPro account.");
            return;
          }
        }
        setStatus("success");
        setMessage(invited ? "Your email has been linked to your DrillLogPro account. Choose a password to finish setup." : "Your email has been verified. Redirecting to DrillLogPro...");
        setTimeout(() => navigate(invited ? "/reset-password" : "/", { replace: true }), 700);
        return;
      }

      // If the provider returned a PKCE code rather than a hash session,
      // explicitly exchange it.
      const code = new URLSearchParams(window.location.search).get("code");
      if (code) {
        const { error: exchangeError } = await authClient.exchangeCodeForSession(code);
        if (!active) return;
        if (!exchangeError) {
          let invited = false;
          try {
            const { data: sessionData } = await authClient.getSession();
            const metadata = sessionData?.session?.user?.user_metadata || {};
            invited = Boolean(metadata.dlp_local_user_id);
            if (invited) await base44.functions.invoke("acceptInvite", {});
          } catch (inviteError) {
            if (invited) { setStatus("error"); setMessage(inviteError?.message || "We could not bind this email to the invited DrillLogPro account."); return; }
          }
          setStatus("success");
          setMessage(invited ? "Your email has been linked to your DrillLogPro account. Choose a password to finish setup." : "Your email has been verified. Redirecting to DrillLogPro...");
          setTimeout(() => navigate(invited ? "/reset-password" : "/", { replace: true }), 700);
          return;
        }
        setStatus("error");
        setMessage(exchangeError.message);
        return;
      }

      setStatus("error");
      setMessage("The verification link is invalid or has expired. Request a new verification email and try again.");
    };

    finish();
    return () => {
      active = false;
    };
  }, [navigate]);

  if (status === "loading") {
    return (
      <AuthLayout icon={Loader2} title="Verifying email" subtitle="Please wait">
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AuthLayout>
    );
  }

  if (status === "success") {
    return (
      <AuthLayout icon={CheckCircle2} title="Email verified" subtitle="Your account is ready">
        <p className="text-sm text-foreground text-center">{message}</p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout icon={AlertTriangle} title="Verification failed" subtitle="We couldn't complete email verification">
      <p className="text-sm text-foreground text-center mb-5">{message}</p>
      <Link to="/login" className="block text-center text-primary font-medium hover:underline">
        Return to log in
      </Link>
    </AuthLayout>
  );
}
